// Shared utilities and constants for Teaching / Unterrichtsverwaltung module.
import { SUBJECT_SPLIT_RE, subjectBaseName, subjectFormSuffix } from '@/lib/utils';

export { SUBJECT_SPLIT_RE, subjectBaseName, subjectFormSuffix };

/** Lehrkräfte, die noch unterrichten. Inaktive bleiben an bestehenden Kursen sichtbar, stehen aber nicht mehr zur Auswahl. */
export function selectableTeachers(rows) {
  return (Array.isArray(rows) ? rows : []).filter((t) => !t?.status || t.status === 'active');
}

/**
 * JeKi hat keine feste Obergrenze. Einzelunterricht hat einen Platz,
 * auch wenn der Kurs fälschlich als Zweiergruppe gespeichert ist.
 */
export function belegungsBasis(name, maxCapacity, activeCount) {
  const text = String(name || '');
  const active = Number(activeCount) || 0;
  if (/jeki/i.test(text)) return { belegungen: active, max: null, frei: null };
  const einzel = /einzel/i.test(text) && !/gruppe/i.test(text);
  const parsed = maxCapacity == null || maxCapacity === '' ? null : Number(maxCapacity);
  const max = einzel ? 1 : (Number.isNaN(parsed) ? null : parsed);
  const frei = max == null ? null : Math.max(0, max - active);
  return { belegungen: active, max, frei };
}

// GKZ (Gemeindekennzeichen) - Musikschulfilialen/Standorte
export const GKZ_OPTIONS = [
  { value: 'GRE', label: 'Greven' },
  { value: 'MS', label: 'Münster' },
  { value: 'BB', label: 'Bad Bentheim' },
  { value: 'SCH', label: 'Schüttorf' },
  { value: 'Schü', label: 'Schüttorf (Schü)' },
  { value: 'BN', label: 'Bonn' },
  { value: 'WFB', label: 'Wolfenbüttel' },
];

// Areal - Bundesland/Region
export const AREAL_OPTIONS = [
  { value: 'NRW', label: 'NRW' },
  { value: 'NDS', label: 'Niedersachsen' },
];

const NDS_CODES = new Set(['NDS', 'NS', 'NI']);
const NRW_CODES = new Set(['NRW', 'NW']);

/** iMikel/Altbestand: NS → NDS, NW → NRW */
export function normalizeArealCode(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const key = raw.toUpperCase();
  if (NDS_CODES.has(key)) return 'NDS';
  if (NRW_CODES.has(key)) return 'NRW';
  return raw;
}

const NDS_TENANT_SLUGS = new Set([
  'MA-OBG',
  'MBS-WF',
  'MA-WOLFENBUETTEL',
  'MA-WOLFENBUETTEL',
]);

/** Standard-Areal je Mandant (Obergrafschaft = Niedersachsen). */
export function defaultArealForTenant(tenant) {
  const slug = normalizeTenantSlug(tenant?.slug);
  if (NDS_TENANT_SLUGS.has(slug)) return 'NDS';
  if (slug === 'MA-GRE' || slug === 'MA-BN') return 'NRW';
  const name = String(tenant?.name || '').toLowerCase();
  if (name.includes('obergraf') || name.includes('wolfen')) return 'NDS';
  if (name.includes('greven') || name.includes('bonn')) return 'NRW';
  return 'NRW';
}

export function resolveAreal(value, tenant) {
  const normalized = normalizeArealCode(value);
  if (normalized === 'NDS' || normalized === 'NRW') return normalized;
  if (String(value || '').trim()) return String(value).trim();
  return defaultArealForTenant(tenant);
}

// Helper to get label from value
export const getGkzLabel = (value) =>
  GKZ_OPTIONS.find((o) => o.value === value)?.label || value || '-';

export const getArealLabel = (value) => {
  const code = normalizeArealCode(value);
  if (code === 'NDS') return 'Niedersachsen';
  if (code === 'NRW') return 'NRW';
  return AREAL_OPTIONS.find((o) => o.value === value)?.label || value || '-';
};

/** Anzeige-Kürzel für das iMikel-Feld „Mand.“ */
export const MANDANT_KUERZEL = {
  greven: 'MAG',
  obergrafschaft: 'MAO',
  bonn: 'MAH',
  wolfenbuettel: 'MAW',
};

const SLUG_TO_KUERZEL = {
  'MA-GRE': MANDANT_KUERZEL.greven,
  'MA-OBG': MANDANT_KUERZEL.obergrafschaft,
  'MA-BN': MANDANT_KUERZEL.bonn,
  'MA-WOLFENBUETTEL': MANDANT_KUERZEL.wolfenbuettel,
  'MBS-WF': MANDANT_KUERZEL.wolfenbuettel,
};

const PREFIX_TO_KUERZEL = {
  GRE: MANDANT_KUERZEL.greven,
  MAG: MANDANT_KUERZEL.greven,
  OBG: MANDANT_KUERZEL.obergrafschaft,
  MAO: MANDANT_KUERZEL.obergrafschaft,
  BN: MANDANT_KUERZEL.bonn,
  MAH: MANDANT_KUERZEL.bonn,
  WF: MANDANT_KUERZEL.wolfenbuettel,
  MAW: MANDANT_KUERZEL.wolfenbuettel,
};

function normalizeTenantSlug(slug) {
  return String(slug || '')
    .toUpperCase()
    .replace(/Ü/g, 'UE')
    .replace(/Ä/g, 'AE')
    .replace(/Ö/g, 'OE')
    .replace(/ß/g, 'SS');
}

export function getMandantKuerzel(tenant, gkz) {
  const slug = normalizeTenantSlug(tenant?.slug);
  if (SLUG_TO_KUERZEL[slug]) return SLUG_TO_KUERZEL[slug];

  const prefix = String(tenant?.invoice_prefix || '').toUpperCase().replace(/\.$/, '');
  if (PREFIX_TO_KUERZEL[prefix]) return PREFIX_TO_KUERZEL[prefix];

  const name = String(tenant?.name || '').toLowerCase();
  if (name.includes('greven')) return MANDANT_KUERZEL.greven;
  if (name.includes('obergraf')) return MANDANT_KUERZEL.obergrafschaft;
  if (name.includes('bonn') || name.includes('henneberg')) return MANDANT_KUERZEL.bonn;
  if (name.includes('wolfen')) return MANDANT_KUERZEL.wolfenbuettel;

  const g = String(gkz || '').toUpperCase();
  if (g === 'GRE' || g === 'MS') return MANDANT_KUERZEL.greven;
  if (g === 'BB' || g === 'SCH') return MANDANT_KUERZEL.obergrafschaft;
  if (g === 'BN') return MANDANT_KUERZEL.bonn;
  if (g === 'WFB' || g === 'WF') return MANDANT_KUERZEL.wolfenbuettel;

  return prefix || '—';
}

/**
 * Anzeige-PK: Mandantenkürzel + nur Ziffern, z. B. MAG48215.
 * Abgeleitet stabil aus der UUID (Hex → Dezimal, letzte N Stellen).
 */
export function formatPersonPk(tenant, gkz, id, digits = 5) {
  if (!id) return '—';
  const mand = getMandantKuerzel(tenant, gkz);
  const hex = String(id).replace(/-/g, '').replace(/[^0-9a-fA-F]/g, '');
  if (!hex) return '—';
  let numStr;
  try {
    // 15 Hex-Zeichen → ausreichend für stabile Dezimalziffern, BigInt-sicher
    numStr = BigInt(`0x${hex.slice(0, 15)}`).toString();
  } catch {
    numStr = hex.replace(/\D/g, '') || '0';
  }
  const short = numStr.slice(-digits).padStart(digits, '0');
  if (!mand || mand === '—') return short;
  return `${mand}${short}`;
}

// Helper to convert ISO datetime to YYYY-MM-DD for HTML <input type="date"> fields.
export const toDateInputValue = (dateValue) => {
  if (!dateValue) return '';
  // Already in YYYY-MM-DD form
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};
